export const useAuthUser = () => {
  const user = useState<any | null>("auth:user", () => null);

  const loggedIn = computed(() => !!user.value?.id);
  const isAdmin = computed(() => user.value?.roleId === 1);
  const isDealer = computed(() => user.value?.roleId === 2);
  const isBackOffice = computed(() => user.value?.roleId === 3);
  const viewName = computed(() => user.value?.username);
  const getSubscriptionTypeId = computed(() => user.value?.client?.subscription);
  const getSubscriptionLabel = computed(() => user.value?.client?.subscription);

  const canAccess = (requestRoles: number[]) => {
    if (!user.value) return false;
    return requestRoles.includes(user.value?.roleId);
  }

  const updateUser = (payload: any) => {
    user.value = payload;
  }

  const loadUser = async () => {
    const { $client } = useNuxtApp()
    const onceOpened = useState('security-force-change-password', () => false)
    onceOpened.value = false;
    try {
      const res = await $client.user.me.query();
      const { preferences } = res
      updateAppConfig({ preferences })
      updateUser(res);
    } catch (e: Error) {
      console.log('e', e.message)
    }
  }

  const login = async (payload: { username: string; password: string }) => {
    const { $client } = useNuxtApp();
    const res = await $client.auth.login.mutate({
      ...payload,
    });

    if (res.requiresOtpChannelSelection) {
      return {
        requiresOtpChannelSelection: true,
        preSessionId: res.preSessionId,
        channels: res.channels,
      };
    }

    if (res.requiresOtp) {
      return { requiresOtp: true, otpSessionId: res.otpSessionId, otpChannelType: res.otpChannelType };
    }

    await loadUser()
    return { requiresOtp: false };
  }

  const verifyOtp = async (payload: { otpSessionId: string; code: string }) => {
    const { $client } = useNuxtApp();
    await $client.auth.verifyOtp.mutate(payload);
    await loadUser()
  }

  const reload = async () => {
    const { $client } = useNuxtApp();
    await $client.auth.accessToken.mutate({});
    await loadUser()
  }

  const clearUser = () => {
    user.value = null;
    const tokenExpiresAt = useCookie('tokenExpiresAt');
    tokenExpiresAt.value = null;
    const refreshTokenExpiresAt = useCookie('refreshTokenExpiresAt');
    refreshTokenExpiresAt.value = null;
  }

  const logout = async (redirectTo: string = '') => {
    const { $client } = useNuxtApp();
    try {
      await $client.auth.logout.mutate();
    } catch (e) {
      console.log(e);
    }
    clearUser();
    if (redirectTo) {
      return navigateTo(`/auth/login?redirect=${encodeURIComponent(redirectTo)}`);
    } else {
      navigateTo('/auth/login');
    }
  }

  // reactive() mirrors the previous Pinia store proxy: auto-unwraps refs and keeps destructuring behavior identical
  return reactive({
    user,
    loggedIn,
    isAdmin,
    isDealer,
    isBackOffice,
    viewName,
    getSubscriptionTypeId,
    getSubscriptionLabel,
    canAccess,
    loadUser,
    updateUser,
    login,
    verifyOtp,
    reload,
    logout,
    clearUser,
  });
}
